import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const UserBasicInfoSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  email: z.string().nullable().optional(),
});

export const UsersByIdsInputSchema = z.object({
  ids: z.array(UUIDSchema).min(1).max(500),
});

export const UsersByIdsResponseSchema = z.array(UserBasicInfoSchema);

/**
 * Compact org-user listing for picker UIs. Returns every active user in
 * the caller's organization, sorted by name. Caps at 500 — orgs that
 * exceed this need a paginated/searchable variant later.
 */
export const ListUsersInOrgOutputSchema = z.array(UserBasicInfoSchema);

/**
 * Org users who hold full Product access (create + read + update + delete on
 * the Product subject). Used to populate assignee pickers for item-master
 * change sets. Same compact shape as {@link ListUsersInOrgOutputSchema}.
 */
export const ListProductAssigneesOutputSchema = z.array(UserBasicInfoSchema);

export const usersContracts = {
  byIds: oc
    .input(UsersByIdsInputSchema)
    .output(UsersByIdsResponseSchema)
    .route({
      method: "GET",
      summary: "Get users by ID",
      description:
        "Returns name and email for up to 500 users in the organization, given their IDs.",
    }),

  listInOrg: oc.input(z.object({})).output(ListUsersInOrgOutputSchema).route({
    method: "GET",
    summary: "List users in my organization",
    description:
      "Returns up to 500 active users in the organization, sorted by name, with ID, name and email, for user pickers.",
  }),

  listProductAssignees: oc.output(ListProductAssigneesOutputSchema).route({
    method: "GET",
    summary: "List org users with full Product permissions (assignees)",
    description:
      "Returns organization users who can create, read, update and delete products, for assigning item master change sets.",
  }),
} as const;

export type UserBasicInfo = z.infer<typeof UserBasicInfoSchema>;
export type UsersByIdsInput = z.infer<typeof UsersByIdsInputSchema>;
export type UsersByIdsResponse = z.infer<typeof UsersByIdsResponseSchema>;
export type ListUsersInOrgOutput = z.infer<typeof ListUsersInOrgOutputSchema>;
export type ListProductAssigneesOutput = z.infer<
  typeof ListProductAssigneesOutputSchema
>;
